import { Accordion, AccordionSummary, Box, CircularProgress, Grid, IconButton, Menu, MenuItem } from "@material-ui/core";
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import React, { Fragment, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import ImageLibrarySearchbar from "./ImageLibrarySearchbar";
import FolderComp from "./FolderComp";
import { Waypoint } from "react-waypoint";
import { makeStyles } from "@material-ui/core";
import { CreateCSSProperties } from "@material-ui/core/styles/withStyles";
import UploadImageButton from "./UploadImage";

const Configuration     = require("shared/services/Configuration");
const Storage           = require("shared/services/Storage");

const fetchLimit = 20;

interface C3ImageLibraryProps {
    handleImageClick: (imageModel: any) => void;
    handleDragStart: (folderPath: string, accordianTitle: string) => void;
    folderPath: string;
    accordianTitle: string;
}

const useStyles = makeStyles<CreateCSSProperties>(() => ({
    accordion: {
        '&.MuiPaper-elevation1': {
            boxShadow: 'none'
        }
    },
    accordionSummary: {
        '&.MuiAccordionSummary-root': {
            minHeight: '3vh',
        },
        '& .MuiAccordionSummary-content': {
            margin: 0
        },
        '& .MuiAccordionSummary-content.Mui-expanded': {
            margin: 0
        },
        '&.MuiAccordionSummary-root.Mui-expanded': {
            minHeight: '3vh'
        }
    },
    accordionContainer: {
        border: '1px solid #94A3B8',
        borderRadius: '4px',
        marginTop: '1vh',
        background: '#FFFFFF',
        boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
    },
    iconBtn: {
        padding: '0.3vh 0'
    },
    folderCompContainer: {
        height: 'auto',
        maxHeight: '23vh',
        overflow: 'auto',
        position: 'absolute',
        zIndex: 2,
        width: '100%',
        top: '4vh',
        border: '1px solid #94A3B8',
        borderRadius: '4px',
        boxShadow: '0px 10px 15px -3px rgba(16, 24, 40, 0.1), 0px 4px 6px -4px rgba(16, 24, 40, 0.1)',
        '& .MuiAccordionSummary-content.Mui-expanded:hover': {
            background: '#E9EBED',
        },
        '& .MuiAccordionSummary-content:hover': {
            background: '#E9EBED'
        }
    },
}
));

export const Cross = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M12 4L4 12"
            stroke="#64748B"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M4 4L12 12"
            stroke="#64748B"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

export const SortIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2 8H7.33333" stroke="#64748B" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M2 4H10.6667" stroke="#64748B" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M2 12H4.66667" stroke="#64748B" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12.6667 4V12" stroke="#64748B" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M15.3333 9.33334L12.6667 12L10 9.33334" stroke="#64748B" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export default function C3ImageLibrary(props: C3ImageLibraryProps) {
    const classes = useStyles();
    const [imageLibraryDialogOpen, setImageLibraryDialogOpen] = useState<boolean>(true);
    const [imageList, setImageList] = useState<any[]>([]);
    const [nestedFolder, setNestedFolder] = useState<any[]>([]);
    const [imageOffset, setImageOffset] = useState<string | null>(null);
    const [sortBy, setSortby] = useState<string>("modifiedAt");
    const [folderPath, setFolderPath] = useState<string>(props.folderPath || "/");
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const sortMenuOpen = Boolean(anchorEl);
    const [hoveredImage, setHoveredImage] = useState<string>("");
    const [isLoadingNextImages, setLoadingNextImages] = useState<boolean>(false);
    const [accordionTitle, setAccordionTitle] = useState<string>(props.accordianTitle || "Select folder");
    const [expand, setExpand] = useState(false); // for Accordion
    const [sortAscending,setSortAscending]=useState<boolean>(false);//for sorting
    const [textSearchPrefix, setTextSearchPrefix] = useState<string | undefined>(undefined);
    const [onEnterClicked, setOnEnterClicked] = useState<boolean>(false);

    useEffect(() => {
        if (onEnterClicked) {
            getImages(textSearchPrefix);
        }  
    }, [onEnterClicked]);

    useEffect(() => {
        setImageList([]);
        getImages();
    }, [sortBy, folderPath]);

    useEffect(() => {
        getNestedFolders();
    }, []);

    const getImages = (searchPrefix? : string) => {
        const token = Storage.Local.get(Storage.Keys.LOGIN, "token") || Storage.Session.get(Storage.Keys.LOGIN, "token");
        fetch(
            `${Configuration.c3ApibaseUrl}/v1/images?limit=${fetchLimit}&sortBy=${sortBy}&sortAscending=${sortAscending}&folderPath=${folderPath}${
                imageOffset ? `&offset=${imageOffset}` : ""}${searchPrefix ? `&searchPrefix=${searchPrefix}` : ""}`,
            {
                headers: {
                    Authorization: `Bearer ${token}`
                },
                method: "GET"
            }
        )
            .then((res) => res.json())
            .then((imageData) => {
                setImageList((prev) => [...prev, ...imageData.imageDetailsList]);
                setImageOffset(imageData.nextOffset);
                setOnEnterClicked(false);
            })
            .catch((err) => console.log(err));
    };

    const getNestedFolders = () => {
        const token = Storage.Local.get(Storage.Keys.LOGIN, "token") || Storage.Session.get(Storage.Keys.LOGIN, "token");
        fetch(
            `${Configuration.c3ApibaseUrl}/v1/imageFoldersNested?limit=100&folderPath=/`,
            {
                headers: {
                    Authorization: `Bearer ${token}`
                },
                method: "GET"
            }
        )
            .then((res) => res.json())
            .then((nestedFolder) => setNestedFolder((prev) => [...prev, ...nestedFolder.folderTree.subfolders]))
            .catch((err) => console.log(err));
    };

    const handleFolderClick = (folderDetails) => {
        const newPath = `${folderDetails.folderPath}${folderDetails.folderId}`;
        setImageOffset(null);
        setFolderPath(newPath);
        setAccordionTitle(folderDetails.name);
        setExpand(false);
    }

    const handleCloseOnAccordion = (e) => {
        e.stopPropagation();
        setFolderPath('/')
        setAccordionTitle("Select folder")
        setExpand(false)
    }

    const handleSortIconClick = (event: any) => {
        setAnchorEl(event.currentTarget);
    };

    const handleSortIconClose = () => {
        setAnchorEl(null);
    };

    const handleSortChange = (sortBy: string,datasortAscending: boolean) => {
        setImageOffset(null);
        setSortAscending(datasortAscending);
        setSortby(sortBy);
        handleSortIconClose();
    };

    const handleSearch = (searchPrefix: string) => {
        setImageOffset(null);
        setImageList([]);
        setTextSearchPrefix(searchPrefix);
        setOnEnterClicked(true);
    };

    const loadNextImages = async () => {
        if (!imageOffset || isLoadingNextImages) {
            return;
        }
        setLoadingNextImages(true);
        try {
            getImages();
        } catch (err) {
            console.error("Failed to fetch images", err);
        }
        setLoadingNextImages(false);
    };

    const handleImageClick = (imageModel) => {
        props.handleImageClick(imageModel);
    }

    const dragStartHandler = (event, imageModel, currentfolderPath, currentaccordianTitle) => {
        const data = {
            type: "image",
            options: {
                imageID: imageModel.imageId,
                altText: imageModel.altText,
                height: imageModel.height,
                width: imageModel.width,
                originalImageHeight: imageModel.height,
                originalImageWidth: imageModel.width,
                src: imageModel.url,
            }
        };

        props.handleDragStart(currentfolderPath, currentaccordianTitle);
        event.dataTransfer.setData("Text", JSON.stringify(data));
    }

    const renderLoader = () => (
        /* @ts-ignore */
        <Box
            style={{ display: "flex", justifyContent: "center", marginBottom: "4px" }}
        >
            <CircularProgress size={25} color="inherit" />
        </Box>
    );

    const renderSortMenu = () => (
        <>
            <IconButton
                id="sort-menu-button"
                style={{ border: "1px solid #94A3B8", borderRadius: "2px", height: "3vh", width: "1.6vw", padding: 0 }}
                aria-controls={sortMenuOpen ? 'sort-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={sortMenuOpen ? 'true' : undefined}
                onClick={handleSortIconClick}
            >
                <SortIcon />
            </IconButton>
            <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={sortMenuOpen}
                onClose={handleSortIconClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
            >
                <MenuItem style={{ fontSize: "14px", fontWeight: 700, opacity: 1 }} disabled onClick={handleSortIconClose}>Sort by</MenuItem>
                <MenuItem style={{ fontSize: "14px" }} selected={sortBy === "modifiedAt"} onClick={() => handleSortChange("modifiedAt",false)}>Last updated</MenuItem>
                <MenuItem style={{ fontSize: "14px" }} selected={sortBy === "name"} onClick={() => handleSortChange("name",true)}>Alphabetical</MenuItem>
            </Menu>
        </>
    )

    const renderImageLibraryDialog = () => {
        return (
            <div style={{ background: "#FFFFFF", width: "100%", maxHeight: "76vh", padding: "8px", position: "absolute", zIndex: 1, left: 0, marginTop: "4px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", paddingBottom: "8px", borderBottom: "1px solid #E2E8F0" }}>
                    <span style={{ fontSize: "16px", lineHeight: "14px", fontWeight: 400, color: "#475569" }}>
                        Images
                    </span>
                    <Button
                        style={{ margin: 0, height: "1.7vh", minWidth: "1vw", lineHeight: "1.7vh", padding: 0 }}
                        onClick={() => setImageLibraryDialogOpen(false)}
                        edge="start"
                    >
                        <Cross />
                    </Button>
                </div>
                <Grid container wrap="nowrap" direction='column' >
                    <div className={classes.accordionContainer} >
                        <Accordion expanded={expand} className={classes.accordion} onChange={() => setExpand(!expand)} >
                            <AccordionSummary className={classes.accordionSummary} >
                                <Grid container justifyContent="space-between" wrap="nowrap" alignItems="baseline" >
                                    <span style={{ color: '#64748B', fontWeight: 400, fontSize: '1.2rem' }} >{accordionTitle}</span>
                                    <Grid item alignItems="center" >
                                        {folderPath !== '/' ?
                                            <IconButton
                                                style={{ marginRight: "1vw" }}
                                                className={classes.iconBtn}
                                                onClick={handleCloseOnAccordion}>
                                                <CloseRoundedIcon />
                                            </IconButton>
                                            : null}
                                        <IconButton className={classes.iconBtn}
                                            onClick={() => setExpand(!expand)}
                                        >
                                            <ExpandMoreIcon fontSize="large" />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            </AccordionSummary>
                            <div className={classes.folderCompContainer} >
                                <FolderComp folderList={nestedFolder} handleOnFolderClick={handleFolderClick} parentFolderId='' />
                            </div>
                        </Accordion>
                    </div>
                    <ImageLibrarySearchbar
                    placeholder={"ex: experience name"} 
                    handleSearch={handleSearch}
                    fetchLimit={fetchLimit}
                    sortBy={sortBy}
                    folderPath={folderPath}
                    />
                </Grid>
                <Grid container justifyContent="space-between" alignItems="center" style={{ margin: '1vh 0px 0.5vh 0px' }} >
                    {renderSortMenu()}
                    <UploadImageButton setImageList={setImageList} folderPath={folderPath} />
                </Grid>
                <div style={{ height: "60vh", overflow: "auto" }}>
                    {imageList.map((image, index) => {
                        if (index === imageList.length - 1) {
                            return (
                                <Fragment key={index}>
                                    {/* @ts-ignore */}
                                    <Waypoint onEnter={() => loadNextImages()}>
                                        <div style={{ position: "relative", width: "50%", display: "inline", cursor: "pointer" }}>
                                            <img
                                                key={image.imageId}
                                                id={image.imageId}
                                                src={image.url}
                                                style={{ height: "9.13vh", width: "50%", padding: "4px 8px 4px 0" }}
                                                onMouseEnter={() => setHoveredImage(image.imageId)}
                                                onMouseLeave={() => setHoveredImage("")}
                                                onClick={() => handleImageClick(image)}
                                                onDragStart={(e) => dragStartHandler(e, image, folderPath, accordionTitle)}
                                            ></img>
                                            <span style={{
                                                display: hoveredImage === image.imageId ? "block" : "none",
                                                background: "linear-gradient(0deg, #363030 -45.83%, #3E39395D 63.54%, #423E3E00 100%)",
                                                width: "95%",
                                                paddingLeft: "4px",
                                                position: "absolute",
                                                textOverflow: "ellipsis",
                                                overflow: "hidden",
                                                whiteSpace: "nowrap",
                                                color: "white",
                                                top: "3.13vh",
                                                left: 0
                                            }}
                                                onMouseEnter={() => setHoveredImage(image.imageId)}>
                                                {image.name}
                                            </span>
                                        </div>
                                    </Waypoint>
                                    {isLoadingNextImages && renderLoader()}
                                </Fragment>
                            );
                        } else {
                            return (
                                <div style={{ position: "relative", width: "50%", display: "inline", cursor: "pointer" }}>
                                    <img
                                        key={image.imageId}
                                        id={image.imageId}
                                        src={image.url}
                                        style={{ height: "9.13vh", width: "50%", padding: "4px 8px 4px 0" }}
                                        onMouseEnter={() => setHoveredImage(image.imageId)}
                                        onMouseLeave={() => setHoveredImage("")}
                                        onClick={() => handleImageClick(image)}
                                        onDragStart={(e) => dragStartHandler(e, image, folderPath, accordionTitle)}
                                    ></img>
                                    <span style={{
                                        display: hoveredImage === image.imageId ? "block" : "none",
                                        background: "linear-gradient(0deg, #363030 -45.83%, #3E39395D 63.54%, #423E3E00 100%)",
                                        width: "95%",
                                        paddingLeft: "4px",
                                        position: "absolute",
                                        textOverflow: "ellipsis",
                                        overflow: "hidden",
                                        whiteSpace: "nowrap",
                                        color: "white",
                                        top: "3.13vh",
                                        left: 0
                                    }}
                                        onMouseEnter={() => setHoveredImage(image.imageId)}>
                                        {image.name}
                                    </span>
                                </div>
                            );
                        }
                    })}
                </div>
            </div>
        );
    }

    return (
        <>
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 600 }}>
                <span>
                    Creator Images
                </span>
                <Button
                    style={{ background: "#8A8A8A", height: "2.43vh", width: "5.27vw", color: "#FAFAFA", fontSize: "14px", lineHeight: "2.43vh", margin: 0 }}
                    onClick={() => setImageLibraryDialogOpen(true)}>
                    Library
                </Button>
            </div>
            {imageLibraryDialogOpen && renderImageLibraryDialog()}
        </>
    );
}


// WEBPACK FOOTER //
// ./src/editor/ui/editor/imageLibrary/C3ImageLibrary.tsx